import React, { useState, useEffect, useContext } from "react";
import { getClientConfig } from "../services/clientConfigCache";
import { ThemeContext } from "../context/ThemeContext";

const ProductImage = ({ codice, descriz, immagine, style: propsStyle, ...props }) => {
  const { darkMode } = useContext(ThemeContext);
  const [config, setConfig] = useState(null);
  const [imgSrc, setImgSrc] = useState('');
  const [triedUppercase, setTriedUppercase] = useState(false);
  const [triedLowercaseCode, setTriedLowercaseCode] = useState(false);
  const [triedLegacyImmagine, setTriedLegacyImmagine] = useState(false);
  const [triedProdotti, setTriedProdotti] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const host = window.location.host;
  const cartellaClientFromHost = host.replace(/[:.]/g, '_');
  const fallbackSrc = "/images/fixedPictures/breadIcon.jpg";
  
  // Usa la cache condivisa: una sola GET /client-config per tutta l'app
  useEffect(() => {
    getClientConfig()
      .then((cfg) => {
        if (cfg) setConfig(cfg);
        else setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, []);
  
  // Aggiorna l'immagine quando cambia il codice o la config
  useEffect(() => {
    if (!config || !codice) return;
    
    
    let basePath;
    const imagesClientKey =
      config.REACT_APP_IMAGES_CLIENT_KEY ||
      window.REACT_APP_IMAGES_CLIENT_KEY ||
      process.env.REACT_APP_IMAGES_CLIENT_KEY ||
      "";

    const cartellaClient = (imagesClientKey || host).replace(/[:.]/g, "_");

    const backendOrigin =
      config.REACT_APP_DEV_BACKEND_URL ||
      config.REACT_APP_PROD_BACKEND_URL ||
      "";

    const spacesOrigin =
      config.REACT_APP_DO_SPACES_URL ||
      "";

    if (config.REACT_APP_USE_SPACES_STORAGE && spacesOrigin) {
      // Usa Spaces (URL diretto bucket, non CDN) — codice MAIUSCOLO
      basePath = `${spacesOrigin}/panify-cloud/images/clienti/${cartellaClient}/${String(codice).toUpperCase()}`;
    } else {
      // Usa storage "locale" (ma nel nostro caso può stare sul backend online):
      // costruisci un URL assoluto verso il backend se disponibile, altrimenti resta relativo.
      const rel = `/images/clienti/${cartellaClient}/${String(codice).toLowerCase()}`;
      basePath = backendOrigin ? `${backendOrigin}${rel}` : rel;
    }
    
    const finalUrl = `${basePath}.jpg`;
    
    setImgSrc(finalUrl);
    setTriedUppercase(false);
    setTriedLowercaseCode(false);
    setTriedLegacyImmagine(false);
    setTriedProdotti(false);
    setIsLoading(false);
  }, [codice, config, host, immagine]);

  const handleError = () => {
    try {
      console.warn("[ProductImage] Immagine non trovata:", {
        codice,
        attemptedSrc: imgSrc,
        host,
        cartellaClientFromHost,
      });
    } catch {
      // ignore logging errors
    }

    if (!triedUppercase) {
      if (imgSrc.toLowerCase().endsWith('.jpg') && imgSrc.endsWith('.jpg')) {
        setImgSrc(imgSrc.replace(/\.jpg$/, '.JPG'));
        setTriedUppercase(true);
        return;
      }
      setTriedUppercase(true);
    }

    // Se stiamo usando Spaces e il file è stato caricato con codice minuscolo,
    // prova anche la variante lowercase prima del fallback.
    const isSpaces = Boolean(
      config?.REACT_APP_USE_SPACES_STORAGE &&
      config?.REACT_APP_DO_SPACES_URL
    );
    if (isSpaces && !triedLowercaseCode && codice != null) {
      const upper = String(codice).toUpperCase();
      const lower = String(codice).toLowerCase();
      if (imgSrc.includes(`/${upper}`)) {
        setImgSrc(imgSrc.replace(`/${upper}`, `/${lower}`));
        setTriedLowercaseCode(true);
        setTriedUppercase(false);
        return;
      }
    }

    // Immagine "precedente" dal gestionale (URL salvato su articolo), se disponibile
    const legacy =
      typeof immagine === 'string' ? immagine.trim() : '';
    if (legacy && !triedLegacyImmagine && imgSrc !== legacy) {
      setImgSrc(legacy);
      setTriedLegacyImmagine(true);
      setTriedUppercase(false);
      return;
    }

    // Prova l'immagine generica condivisa (non per tenant), allineato al README.
    if (!triedProdotti && codice != null) {
      setImgSrc(`/images/prodotti/${String(codice).toLowerCase()}.jpg`);
      setTriedProdotti(true);
      setTriedUppercase(false);
      return;
    }

    setImgSrc(fallbackSrc);
  };
  
  // Immagine default del pane: in dark mode inverti i colori
  const isFallbackImage = imgSrc === fallbackSrc || !imgSrc;
  const baseStyle = { maxHeight: '60px', maxWidth: '80px', ...propsStyle };
  const imgStyle = darkMode && isFallbackImage
    ? { ...baseStyle, filter: 'invert(1)' }
    : baseStyle;

  // Mostra placeholder mentre carica config
  if (isLoading || !imgSrc) {
    return (
      <img
        src={fallbackSrc}
        alt={descriz}
        style={imgStyle}
        className="img-thumbnail"
        {...props}
      />
    );
  }

  return (
    <img
      src={imgSrc}
      alt={descriz}
      style={imgStyle}
      className="img-thumbnail"
      onError={handleError}
      {...props}
    />
  );
};

export default ProductImage;